import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Make -> Model -> Variant, chosen by the visitor (owner, 24 Sep).
 *
 * PROD: UP16CM1247 came back from the registry as "MAHINDRA SCORPIO S9 MH 2W
 * 140" with NO variant match and no candidates, the journey went on anyway, and
 * the quote page said "We could not identify the vehicle, so there was nothing
 * to price" -- a dead end. A quote cannot be priced without a variant, so when
 * the registry leaves us without one (and when the visitor goes on without the
 * registry at all) they pick it here, from the same master the broker's wizard
 * uses, before continuing.
 *
 * THREE PUBLIC, READ-ONLY CATALOGUE ENDPOINTS (apps/vehicles/views_public.py;
 * unauthenticated, bare axios like the rest of the public journey):
 *   GET /policy/api/public/vehicles/catalogue/makes/
 *   GET /policy/api/public/vehicles/catalogue/makes/<make_id>/models/
 *   GET /policy/api/public/vehicles/catalogue/models/<model_id>/variants/?fuel=<fuel>
 * They answer `{results: [...]}` (a bare list is accepted too). Makes and
 * models are de-duplicated by name server-side; a variant's `label` carries
 * its fuel / cc / gearbox so two same-named rows never read alike.
 *
 * NEVER A NEW DEAD END. The choice is required while the catalogue answers.
 * If it cannot be loaded, or a model has no variants at all, the visitor may go
 * on without one -- the request still reaches the advisor, who confirms the
 * vehicle -- and the page says so rather than trapping them.
 */

const API_ROOT = '/policy/api/public/vehicles/catalogue';

interface CatalogueRow {
  id: string;
  name: string;
  label?: string;
}

export interface ManualVehicleChoice {
  variantId: string;
  makeName: string;
  modelName: string;
  variantName: string;
}

function rowsOf(data: unknown): CatalogueRow[] {
  const list = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return list
    .filter((r): r is CatalogueRow => !!r && typeof r === 'object' && 'id' in r && 'name' in r)
    .map((r) => ({ id: String(r.id), name: String(r.name), label: r.label ? String(r.label) : undefined }));
}

const normalise = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '');

/** The make the registry named, matched on the catalogue's own names. */
export function matchMake(makes: CatalogueRow[], registryName: string | undefined): string {
  const wanted = normalise(registryName ?? '');
  if (!wanted) return '';
  const exact = makes.find((m) => normalise(m.name) === wanted);
  if (exact) return exact.id;
  // "MAHINDRA & MAHINDRA LIMITED" vs "Mahindra": the longest catalogue name
  // the registry's text starts with.
  const prefixed = makes
    .filter((m) => normalise(m.name) && wanted.startsWith(normalise(m.name)))
    .sort((a, b) => normalise(b.name).length - normalise(a.name).length);
  return prefixed[0]?.id ?? '';
}

const selectClass =
  'mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60';

type Load = 'loading' | 'ready' | 'failed';

export function ManualVehiclePicker({
  initialMakeId,
  initialMakeName,
  fuel,
  onConfirm,
  onSkip,
}: {
  initialMakeId?: string | null;
  /** The registry's make text, for pre-selection when no id matched. */
  initialMakeName?: string;
  /** The registry's fuel, sent as the variants filter. */
  fuel?: string;
  onConfirm: (choice: ManualVehicleChoice) => void;
  /** Offered ONLY when the catalogue cannot give the visitor a choice. */
  onSkip: () => void;
}) {
  const [makes, setMakes] = useState<CatalogueRow[]>([]);
  const [models, setModels] = useState<CatalogueRow[]>([]);
  const [variants, setVariants] = useState<CatalogueRow[]>([]);
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [makesLoad, setMakesLoad] = useState<Load>('loading');
  const [modelsLoad, setModelsLoad] = useState<Load>('ready');
  const [variantsLoad, setVariantsLoad] = useState<Load>('ready');
  const [fuelRelaxed, setFuelRelaxed] = useState(false);
  const initial = useRef({ initialMakeId, initialMakeName });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_ROOT}/makes/`)
      .then((res) => {
        if (cancelled) return;
        const rows = rowsOf(res.data);
        setMakes(rows);
        setMakesLoad(rows.length > 0 ? 'ready' : 'failed');
        const { initialMakeId: id, initialMakeName: name } = initial.current;
        const preset = id && rows.some((m) => m.id === id) ? id : matchMake(rows, name);
        if (preset) setMakeId(preset);
      })
      .catch(() => {
        if (!cancelled) setMakesLoad('failed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setModels([]);
    setModelId('');
    if (!makeId) return;
    let cancelled = false;
    setModelsLoad('loading');
    axios
      .get(`${API_ROOT}/makes/${encodeURIComponent(makeId)}/models/`)
      .then((res) => {
        if (cancelled) return;
        setModels(rowsOf(res.data));
        setModelsLoad('ready');
      })
      .catch(() => {
        if (!cancelled) setModelsLoad('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [makeId]);

  useEffect(() => {
    setVariants([]);
    setVariantId('');
    setFuelRelaxed(false);
    if (!modelId) return;
    let cancelled = false;
    setVariantsLoad('loading');
    const url = (withFuel: boolean) =>
      `${API_ROOT}/models/${encodeURIComponent(modelId)}/variants/${
        withFuel && fuel ? `?fuel=${encodeURIComponent(fuel)}` : ''
      }`;
    (async () => {
      try {
        let rows = rowsOf((await axios.get(url(true))).data);
        // The registry's fuel words and the catalogue's can disagree
        // ("PETROL/CNG"); an empty filtered list is not "no variants".
        if (rows.length === 0 && fuel) {
          rows = rowsOf((await axios.get(url(false))).data);
          if (!cancelled && rows.length > 0) setFuelRelaxed(true);
        }
        if (cancelled) return;
        setVariants(rows);
        setVariantsLoad('ready');
      } catch {
        if (!cancelled) setVariantsLoad('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId, fuel]);

  const cannotChoose =
    makesLoad === 'failed' ||
    modelsLoad === 'failed' ||
    variantsLoad === 'failed' ||
    (modelsLoad === 'ready' && !!makeId && models.length === 0) ||
    (variantsLoad === 'ready' && !!modelId && variants.length === 0);

  const confirm = () => {
    const make = makes.find((m) => m.id === makeId);
    const model = models.find((m) => m.id === modelId);
    const variant = variants.find((v) => v.id === variantId);
    if (!make || !model || !variant) return;
    onConfirm({
      variantId: variant.id,
      makeName: make.name,
      modelName: model.name,
      variantName: variant.label ?? variant.name,
    });
  };

  return (
    <div data-testid="manual-vehicle-picker">
      <p>
        Choose your vehicle from the list -- we need the exact variant to fetch a price. It is on
        your registration certificate.
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label htmlFor="manual-make" className="block text-sm font-medium">
            Make
          </label>
          <select
            id="manual-make"
            className={selectClass}
            value={makeId}
            disabled={makesLoad !== 'ready'}
            onChange={(e) => setMakeId(e.target.value)}
          >
            <option value="">{makesLoad === 'loading' ? 'Loading...' : 'Choose the make'}</option>
            {makes.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label ?? m.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="manual-model" className="block text-sm font-medium">
            Model
          </label>
          <select
            id="manual-model"
            className={selectClass}
            value={modelId}
            disabled={!makeId || modelsLoad !== 'ready'}
            onChange={(e) => setModelId(e.target.value)}
          >
            <option value="">{modelsLoad === 'loading' ? 'Loading...' : 'Choose the model'}</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label ?? m.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="manual-variant" className="block text-sm font-medium">
            Variant
          </label>
          <select
            id="manual-variant"
            className={selectClass}
            value={variantId}
            disabled={!modelId || variantsLoad !== 'ready'}
            onChange={(e) => setVariantId(e.target.value)}
          >
            <option value="">{variantsLoad === 'loading' ? 'Loading...' : 'Choose the variant'}</option>
            {variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label ?? v.name}
              </option>
            ))}
          </select>
          {fuelRelaxed && (
            <p className="mt-1 text-xs">
              No variant of this model matched the registry&rsquo;s fuel ({fuel}), so all of them are
              listed. Please pick the one on your registration certificate.
            </p>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={confirm}
        disabled={!variantId}
        className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        Continue with this vehicle
      </button>

      {cannotChoose && (
        <div role="status" className="mt-3 text-sm">
          <p>
            {makesLoad === 'failed' || modelsLoad === 'failed' || variantsLoad === 'failed'
              ? 'We could not load the vehicle list just now.'
              : 'Your vehicle is not in our list.'}{' '}
            You can still send your request -- your advisor will confirm the vehicle and price it
            for you.
          </p>
          <button
            type="button"
            onClick={onSkip}
            className="mt-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Continue -- my advisor will confirm the vehicle
          </button>
        </div>
      )}
    </div>
  );
}
